import { useEffect, useState } from "react";
import { ReportSummaryItemApi, SourceTypeFilter } from "../api/Reports";
import { useFetchItemsForSummary } from "../api/ItemsApi";
import {
  FlagCountResponse,
  GroupCount,
  ItemsForReviewRequest,
  ItemsForReviewSortCriteria,
  ItemsForSummaryRequest,
  ItemWithMetaData,
} from "../api/Items";
import {
  buildReportItemsFilterRequests,
  getColorCode,
  postAndArticleCountsFromFlagCount,
  processFlagCountForDisplay,
} from "../reports/utils";
import { DonutChart } from "../shared-components/DonutChart";
import Divider from "../shared-components/LineDivider";
import { useReportDetail } from "../reports/ReportDetailProvider";
import { useDebounce } from "../utils";
import {
  countFilterKey,
  reviewRequestMatchesFilters,
  trackCountResponses,
  useFilteredCountGeneration,
} from "../reports/ReportSidebar/useFilteredCountGeneration";
import { behaviorMap, useFamaFlags } from "../api/FamaFlags";
import { useFetchReportRerun } from "../api/ReportsApi";
import { PostsAuditQA } from "../posts/PostsAuditQA";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import AuditQAModalAdd from "./AuditQAModalAdd";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import PaginationInMemory from "../shared-components/PaginationInMemory";

const INITIAL_PAGE_SIZE = 25;
const pageSize = 20;

function AuditQAPosts({
  report,
  sourceTypeFilter,
  postTypeFilter,
  dateFilter,
  behaviorFilter,
  keywordFilter,
  topicFilter,
  itemTypeFilter,
  updateFlagCount,
  updateKeywordCount,
}: {
  report: ReportSummaryItemApi;
  isLoadingQaDone: boolean;
  sourceTypeFilter: SourceTypeFilter;
  postTypeFilter: { original: boolean; shared: boolean; liked: boolean };
  dateFilter: { fromDate: string; toDate: string };
  behaviorFilter: string[];
  keywordFilter: string[];
  topicFilter: string[];
  itemTypeFilter: string[];
  updateFlagCount: (flags: FlagCountResponse) => void;
  updateKeywordCount: (keywords: GroupCount) => void;
}) {
  const { isLoading: isLoadingItems, mutateAsync: getPostsAsync } =
    useFetchItemsForSummary();
  const {
    profileList,
    blueprint,
    loadProfiles,
    getFlags,
    getKeywordCount,
    getTopicCount,
    flagCount,
    flagsLoading,
    errorFlag,
    errorKeyword,
    keywordCount,
    keywordsLoading,
    setFilteredCountsReady,
  } = useReportDetail();
  const { mutate: getReruns } = useFetchReportRerun();
  const [sort] = useState<{ id: string; label: string }>({
    id: ItemsForReviewSortCriteria.Date,
    label: "Sort By Date",
  });

  const [filteredPostsList, setFilteredPostsList] = useState<
    ItemWithMetaData[] | undefined
  >([]);

  const { behaviorMappingActions } = useFamaFlags();
  const [flagAmounts, setFlagAmounts] = useState<{ [key: string]: number }>({});
  const [page, setPage] = useState(1);
  const [postListCategory, setPostListCategory] = useState<{
    [key: string]: ItemWithMetaData[];
  }>({});
  const [requestParameters, setRequestParameters] =
    useState<ItemsForReviewRequest>({
      report_uuid: report?.report_uuid ?? "",
      item_type_name: [], // TODO: Check if the pie chart stills accepts no filter to return all
      page_size: pageSize,
      sort_criteria: ItemsForReviewSortCriteria.Date,
    });

  const [requestParametersSummary, setRequestParametersSummary] =
    useState<ItemsForSummaryRequest>({
      report_id: report.report_uuid ?? "",
      item_types: itemTypeFilter,
      behaviors: behaviorFilter,
      keywords: keywordFilter,
      topics: topicFilter,
      sort: sort.id,
      page_size: pageSize,
      page_number: page,
    });
  useEffect(() => {
    loadProfiles?.();
  }, [loadProfiles]);
  function loadPosts(resetPage: boolean = false) {
    let pageQuery = page;
    if (resetPage) {
      setPage(1);
      setFilteredPostsList([]);
      pageQuery = 0;
    }
    return getPostsAsync({
      ...requestParametersSummary,
      page_size: pageQuery === 0 ? INITIAL_PAGE_SIZE : pageSize,
      page_number: pageQuery,
    }).then((posts) => {
      if (resetPage) {
        setFilteredPostsList(posts);
        return;
      }
      setFilteredPostsList(() => [...(posts ?? [])]);
    });
  }
  const debouncedRequestParametersSummary = useDebounce<ItemsForSummaryRequest>(
    requestParametersSummary,
    1000
  );
  const countFilters = countFilterKey({
    reportUuid: report.report_uuid,
    sourceTypeFilter,
    postTypeFilter,
    dateFilter,
    behaviorFilter,
    keywordFilter,
    topicFilter,
    itemTypeFilter,
  });
  const { ready: filteredCountsReady, capture, settle } =
    useFilteredCountGeneration(countFilters);

  useEffect(() => {
    setFilteredCountsReady?.(filteredCountsReady);
  }, [filteredCountsReady, setFilteredCountsReady]);
  useEffect(() => {
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedRequestParametersSummary]);

  useEffect(() => {
    if (!filteredPostsList) return;
    const newPostList: { [key: string]: ItemWithMetaData[] } = {};
    const itemsWithDate: ItemWithMetaData[] = [];
    const itemsWithoutDate: ItemWithMetaData[] = [];

    filteredPostsList.forEach((item) => {
      const createdDate = item.item.content?.created_at;
      if (createdDate) {
        itemsWithDate.push(item);
      } else {
        itemsWithoutDate.push(item);
      }
    });

    if (sort.id === ItemsForReviewSortCriteria.Date) {
      itemsWithDate.forEach((item) => {
        const createdDate = new Date(
          item.item.content?.created_at
        ).getFullYear();
        if (!(createdDate in newPostList)) {
          newPostList[createdDate] = [];
        }
        newPostList[createdDate].push(item);
      });

      if (itemsWithoutDate.length > 0) {
        newPostList["No Date"] = itemsWithoutDate; // Add items without date at the end
      }
    } else {
      var mapPostList = new Map();
      filteredPostsList.forEach((item) => {
        mapPostList.set(item.item.external_id, item);
      });
      mapPostList.forEach((item) => {
        let haveBehavior: boolean = false;
        item.rating.forEach((rating: any) => {
          if (rating.score_type === "behavior") {
            haveBehavior = true;
            if (!(rating.name in newPostList)) {
              newPostList[rating.name] = [];
            }
            newPostList[rating.name].push(item);
          }
        });
        if (!haveBehavior) {
          if (!("No Behavior" in newPostList)) {
            newPostList["No Behavior"] = [];
          }
          newPostList["No Behavior"].push(item);
        }
      });
    }

    setPostListCategory(newPostList);
  }, [filteredPostsList, sort]);

  useEffect(() => {
    if (!report) return;
    const { review, summary } = buildReportItemsFilterRequests({
      report,
      itemTypeFilter,
      behaviorFilter,
      keywordFilter,
      topicFilter,
      sortId: sort.id,
      pageSize,
      summaryPageNumber: page,
      sourceTypeFilter,
      postTypeFilter,
      dateFilter,
    });
    setRequestParameters(review);
    setRequestParametersSummary(summary);
  }, [
    sourceTypeFilter,
    postTypeFilter,
    dateFilter,
    behaviorFilter,
    keywordFilter,
    topicFilter,
    itemTypeFilter,
    sort,
    report,
    page,
  ]);

  useEffect(() => {
    if (!report) return;
    getReruns(report.report_uuid);
    if (
      !reviewRequestMatchesFilters(requestParameters, {
        behaviorFilter,
        keywordFilter,
        topicFilter,
        itemTypeFilter,
        sourceTypeFilter,
        postTypeFilter,
        dateFilter,
      })
    ) {
      return;
    }
    const responses = trackCountResponses(capture(), settle);
    getFlags?.(
      { ...requestParameters, report_uuid: report.report_uuid },
      responses
    );
    getKeywordCount?.(
      {
        ...requestParameters,
        report_uuid: report.report_uuid,
      },
      responses
    );
    getTopicCount?.(
      {
        ...requestParameters,
        report_uuid: report.report_uuid,
      },
      responses
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getFlags,
    getKeywordCount,
    getTopicCount,
    getReruns,
    report,
    debouncedRequestParametersSummary,
  ]);

  useEffect(() => {
    if (!flagCount) return;
    const { processed, flagAmounts: amounts } = processFlagCountForDisplay(
      flagCount,
      behaviorMappingActions
    );
    updateFlagCount(processed);
    setFlagAmounts(amounts);
  }, [flagCount, setFlagAmounts, updateFlagCount, behaviorMappingActions]);

  useEffect(() => {
    if (keywordCount) {
      updateKeywordCount(keywordCount);
    }
  }, [keywordCount, updateKeywordCount]);

  const donutChartValues = Object.keys(flagAmounts).map((key) => {
    return {
      flagCount: flagAmounts[key],
      value: (flagAmounts[key] / (flagCount?.behaviors.total ?? 1)) * 100,
      color: getColorCode(key) || "gray",
      label: key,
    };
  });

  const categorySorted = (keyList: string[]) => {
    if (sort.id === ItemsForReviewSortCriteria.Date) {
      // Sort with "No Date" at the end
      return keyList.sort((a, b) => {
        if (a === "No Date") return 1;
        if (b === "No Date") return -1;
        return b.localeCompare(a);
      });
    }
  };

  const {
    postCount,
    articleCount,
    total: totalCount,
  } = postAndArticleCountsFromFlagCount(flagCount);

  function afterQAAddOrEdit() {
    loadPosts();
    setRequestParametersSummary({
      ...requestParametersSummary,
      page_number: 1,
    });
  }

  function afterPostCardAction() {
    afterQAAddOrEdit();
    getFlags?.({ report_uuid: report.report_uuid });
    getKeywordCount?.({ report_uuid: report.report_uuid });
    getTopicCount?.({ report_uuid: report.report_uuid });
  }

  return (
    <div>
      {/* Chart */}

      <div>
        <div className="p-2 my-4 flex flex-row border">
          <div className="flex flex-nowrap flex-col flex-shrink-0 min-w-0">
            <div className="whitespace-nowrap justify-items-center font-bold self-center ">
              <span className="mr-1">Flag Count:</span>
              {flagCount?.behaviors.total}
            </div>

            <div className="relative p-3">
              <DonutChart
                data={donutChartValues}
                cutout={125}
                enableTooltip={true}
              />
            </div>
          </div>
          {(errorFlag || errorKeyword) && (
            <ErrorMessageContainer message={"Couldn't load flags"} />
          )}
          {(flagsLoading || keywordsLoading) && <LoadingSpinner />}
          {!flagsLoading && !keywordsLoading && (
            <div className="flex flex-nowrap flex-col ml-6 overflow-clip">
              <div className="flex flex-wrap flex-row mb-4 pt-3">
                {donutChartValues
                  .sort(function (a, b) {
                    if (a.label < b.label) {
                      return -1;
                    }
                    if (a.label > b.label) {
                      return 1;
                    }
                    return 0;
                  })
                  .map((value: any, idx) => {
                    return (
                      <div
                        className={`border-l-4 mr-4 p-1 pr-3 mt-1 rounded`}
                        style={{
                          background: `${value.color}33`,
                          borderColor: value.color,
                          color: "black",
                          borderLeftStyle: "solid",
                        }}
                        key={idx}
                      >
                        <span className="capitalize">
                          {value.label.toLowerCase()}
                        </span>{" "}
                        {flagAmounts[value.label]}
                      </div>
                    );
                  })}
              </div>
              {donutChartValues.length === 0 && (
                <>
                  <div>{"There are no behavior flags at this time"}</div>
                  <Divider className="mb-8" />
                </>
              )}

              <div className="flex flex-wrap flex-row mb-4 font-bold">
                <span className="mr-1">Post Count:</span>
                {postCount}
              </div>
              <div className="flex flex-wrap flex-row mb-4 font-bold">
                <span className="mr-1">Article Count:</span>
                {articleCount}
              </div>
              <div className="flex flex-wrap flex-row mb-4 font-bold">
                <span className="mr-1">Keyword Count:</span>
                {keywordCount?.total ?? 0}
              </div>

              <ul className="list-none">
                {profileList?.map((item: ItemWithMetaData, idx) => {
                  return (
                    <li className="my-2" key={idx}>
                      {item.item.content.url}
                    </li>
                  );
                })}
                {(profileList || []).length === 0 && (
                  <li className="my-2" key="1">
                    No Confirmed Profiles found.
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Posts and Articles */}
      {isLoadingItems && <LoadingSpinner />}
      {!isLoadingItems && (
        <div>
          <div className="flex flex-row justify-between">
            <div className="text-lg font-semibold">Posts and Articles</div>
            <div className="flex flex-row">
              <AuditQAModalAdd
                behaviors={blueprint?.behaviors ?? []}
                report={report}
                handleAction={(action) => {
                  if (action === "add") afterQAAddOrEdit();
                }}
              />
            </div>
          </div>
          <div className="my-4">
            {categorySorted(Object.keys(postListCategory))?.map(
              (key: string) => {
                return (
                  <div key={key}>
                    {key && key !== "NaN" && (
                      <div
                        key={key}
                        className="w-full py-2 rounded-md bg-gray-100 font-semibold text-center my-4"
                      >
                        {behaviorMap(key, behaviorMappingActions, "key")}
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 3xl:grid-cols-3 gap-5 auto-cols-[minmax(20rem,_1fr)]">
                      {postListCategory[key]?.map(
                        (item: ItemWithMetaData, idx) => {
                          return (
                            <PostsAuditQA
                              key={idx}
                              post={item}
                              behaviors={blueprint?.behaviors ?? []}
                              handleAction={afterPostCardAction}
                              isReadOnly={false}
                              report={report}
                            ></PostsAuditQA>
                          );
                        }
                      )}
                    </div>
                  </div>
                );
              }
            )}
            <div>
              <PaginationInMemory
                currentPage={page}
                pageSize={pageSize}
                totalCount={totalCount}
                pageChange={(idx: number) => setPage(idx)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuditQAPosts;
